package com.oasisfeng.invaluable.social

import com.oasisfeng.invaluable.NestedLinkable
import com.oasisfeng.invaluable.Quantity
import com.oasisfeng.invaluable.actor.Actor
import com.oasisfeng.invaluable.actor.ActorRoute
import com.oasisfeng.invaluable.actor.Commitment
import com.oasisfeng.invaluable.actor.CommitmentContent
import com.oasisfeng.invaluable.data.LinkProjection
import com.oasisfeng.invaluable.data.Observation
import com.oasisfeng.invaluable.data.ProjectedLinkRecordResolver
import com.oasisfeng.invaluable.data.project
import com.oasisfeng.invaluable.data.projectedRecords
import com.oasisfeng.invaluable.data.resolveLink
import com.oasisfeng.invaluable.dsl.Post
import com.oasisfeng.invaluable.dsl.SocialDsl
import com.oasisfeng.invaluable.dsl.profileOf
import com.oasisfeng.invaluable.event.Capability
import com.oasisfeng.invaluable.event.CapabilityResolver
import com.oasisfeng.invaluable.event.CapabilityWrap
import com.oasisfeng.invaluable.event.Ed25519Credential
import com.oasisfeng.invaluable.event.InMemoryReplicatedEventStore
import com.oasisfeng.invaluable.event.LineWireCodec
import com.oasisfeng.invaluable.event.ReplicatedEvent
import com.oasisfeng.invaluable.event.ReplicatedEventNode
import com.oasisfeng.invaluable.event.SealedContent
import com.oasisfeng.invaluable.event.Sha256LinkProjector
import com.oasisfeng.invaluable.event.publishCommitment
import com.oasisfeng.invaluable.identity.Codec
import com.oasisfeng.invaluable.identity.Identity
import com.oasisfeng.invaluable.identity.SecretBox
import com.oasisfeng.invaluable.identity.SecureRandom
import com.oasisfeng.invaluable.meaning.ActorProfile
import com.oasisfeng.invaluable.meaning.Utf8
import com.oasisfeng.invaluable.p2p.core.DirectedEnvelope
import kotlinx.io.bytestring.ByteString

/**
 * Link-native local social node — the social DSL specialization of the generic
 * [ReplicatedEventNode][com.oasisfeng.invaluable.event.ReplicatedEventNode] it extends.
 *
 * This node keeps replicated facts as its source of truth: the replicated event sets
 * ([store][com.oasisfeng.invaluable.event.ReplicatedEventNode.store])
 * are the single source of truth for the local view, and a local [SocialDsl] venue is merely the "pen" the local user
 * writes commitments with. Local and remote events flow through the same [apply] intake (inherited), so a freshly
 * authored post is observed exactly as a remote peer would observe it.
 *
 * Not thread-safe: confine to one thread or synchronize externally.
 *
 * Persistence and P2P layer *above* the node by re-applying a `WireCodec` export ([encodeAll]/[importLines] on the base
 * node, driven by `SocialClient` / `PeerSync`); [export]/[import] are the seam those layers and tests drive. Valuation
 * (M6) layers above too: [board] feeds the local snapshot to the value field (see `BoardValuation`), so this
 * node stays purely about replicated facts. What this class adds over the base node is everything social: local
 * authoring verbs, selective-reveal private posts, and the [localView] / [board] projection.
 */
class SocialNode(
    val identity: Identity,
    store: InMemoryReplicatedEventStore = InMemoryReplicatedEventStore(Sha256LinkProjector()),
    /** Upper bound on buffered out-of-order attestations; injectable so tests can drive eviction without 4096 events. */
    maxPending: Int = DEFAULT_MAX_PENDING,
) : ReplicatedEventNode(store, maxPending) {

    /** Local-author venue. The store, not this venue, is the source of truth for [localView]. */
    val social: SocialDsl = SocialDsl(projector)

    /** This node's own economic identity, founded on its Ed25519 credential. */
    val localActor: Actor = Actor(Ed25519Credential(identity))

    /**
     * Selective reveal (roadmap §7.1, [L16]): the local actor's *private posts*.
     *
     * A private post is an ordinary commitment authored normally — its full closure is in [store], so this node's own
     * view resolves the body and amount like any post. What differs is replication: its committed **content** is
     * withheld. [privatePosts] maps each unrevealed private post's board ref projection to the commitment heads withheld
     * for it (the post's own head plus any private backs on it — see [publishDark]); [withheldContent] holds those
     * commitments' content projections, which [PeerSync] feeds to the protocol so the catch-up walk skips them. Live
     * gossip already only carries the dark header. Peers see an attributable missing observation, never the body or
     * amount, until [reveal].
     */
    private val privatePosts = mutableMapOf<LinkProjection, MutableSet<LinkProjection>>()
    private val withheldContent = mutableSetOf<LinkProjection>()

    /**
     * Sealed-content disclosure (Milestone 13 / §20.2–20.3).
     *
     * [verifiedSeals] is every content-key this node has *proven* it can read with, keyed by the *content projection* it
     * unlocks — its own authored keys ([publishSealed]) and every key recovered from an inbound [DirectedEnvelope] whose
     * ciphertext was present to verify it against ([receiveEnvelope]) alike. Only these feed a [CapabilityResolver] in
     * [localView] (so proven content resolves like an ordinary post, unproven stays an honest missing observation) and
     * only these fire [onCapabilityLearned]; a key never opens content it cannot make resolve, so it never darkens a
     * working key nor churns a re-render.
     *
     * Keying by content projection is what makes disclosure transitive (review Blocker 2): admission to read a post is
     * exactly *holding its key*, never a membership label, so **any** holder — not only the author — can disclose onward.
     * A key learned from an inbound envelope is indistinguishable here from an authored one, so [recommend] re-wraps it to
     * a further recipient through the same path. The content projection both selects the key (matched against the
     * recommended post's own commitment) and binds the re-wrap to its content ([CapabilityWrap]).
     *
     * [pendingSeals] holds keys that arrived *before* their ciphertext synced, so they cannot yet be verified (a
     * key-before-ciphertext reorder on this best-effort mesh — or an attacker's junk wrap for content we do not hold). A
     * pending key is inert: it never feeds the resolver and never fires a callback, so junk wraps cannot spam re-renders
     * (review §2, a liveness/resource concern, not a leak). Each projection parks every *distinct* candidate it has seen,
     * not just the latest one: with a single slot, a junk wrap arriving after a genuine one (still both unverifiable, the
     * ciphertext not yet synced) would silently overwrite the real key with no way to recover it once the ciphertext
     * finally lands — the same loss the single-slot design was already guarding against in the other arrival order. When
     * the matching ciphertext later arrives ([apply]) every parked candidate is re-verified and whichever genuinely
     * resolves is promoted into [verifiedSeals]; the rest (junk) are discarded with it. Both the outer map and each
     * per-projection candidate list are bounded ([MAX_PENDING_SEALS] / [MAX_PENDING_CANDIDATES], oldest eviction) so
     * unbackable or repeated junk cannot grow memory without limit.
     */
    private val verifiedSeals = mutableMapOf<LinkProjection, Capability>()
    private val pendingSeals = mutableMapOf<LinkProjection, MutableList<Capability>>()
    private val sealCodec by lazy { LineWireCodec(projector.scheme) }

    /** Emit a directed disclosure envelope (set by [PeerSync] to the transport's best-effort flood). */
    var onLocalEnvelope: (DirectedEnvelope) -> Unit = {}

    /** Fired when a received envelope unlocked a new capability, so the host can re-render the now-readable content. */
    var onCapabilityLearned: () -> Unit = {}

    // ───── local authoring ─────

    /**
     * Run one venue [action] as [localActor] and publish the resulting commitment head.
     *
     * Each MVP verb appends exactly one commitment, so the new local head is published and applied back through the
     * same [apply] intake — keeping the local view identical to a remote observer's. Appending more than one commitment
     * in a single action is rejected: only the final head would get a [com.oasisfeng.invaluable.event.SignedCommitment],
     * leaving the intermediate commitments with replicated preimages but no attestation, hence unattributable to peers.
     */
    fun <T> act(action: SocialDsl.(Actor) -> T): T = actWith(::publish, action)

    private fun <T> actWith(publish: (Commitment) -> Unit, action: SocialDsl.(Actor) -> T): T {
        resumeLocalJournal()
        val before = social.snapshot(localActor).commitments.size
        val result = social.action(localActor)
        val commitments = social.snapshot(localActor).commitments
        val appended = commitments.size - before
        require(appended <= 1) {
            "act{} must append at most one commitment, but appended $appended; intermediate heads would replicate " +
                "without an attestation and be unattributable to peers."
        }
        if (appended == 1) publish(commitments.last())
        return result
    }

    fun post(text: String, cost: Quantity): Post = act { me -> me.share(Utf8.encodeDirect(text), cost) }
    fun comment(on: Post, text: String, cost: Quantity): Post = act { me -> me.comment(on, Utf8.encodeDirect(text), cost) }
    fun reply(via: ActorRoute, text: String, cost: Quantity): Post = act { me -> me.reply(via, Utf8.encodeDirect(text), cost) }
    fun endorse(route: ActorRoute, cost: Quantity): Commitment = act { me -> me.endorse(route, cost) }
    fun back(target: NestedLinkable, cost: Quantity): Commitment = act { me -> me.back(target, cost) }

    /**
     * Recommend [post] to [to], routing it to their attention — and, if [post] is sealed content this node holds the
     * key for, **disclose it by forwarding**: emit a [DirectedEnvelope] wrapping the content-key to [to]'s identity
     * (§20.3, KTD5). The route is the durable graph fact; the wrap is a transport-only directed envelope. A recommend
     * of non-sealed (or not-locally-keyed) content is unchanged — no envelope is emitted.
     */
    fun recommend(post: Post, to: Actor, cost: Quantity): ActorRoute {
        val route = act { me -> me.recommend(post, to, cost) }
        discloseSealedTo(post, to)
        return route
    }

    /** Author a *private* post (a withheld commitment, [encodePrivateHeads]); identical to [post] but its content is dark. */
    fun postPrivate(text: String, cost: Quantity): Post = actWith(::publishDark) { me -> me.share(Utf8.encodeDirect(text), cost) }

    /** Back a target with a *private* commitment — the private-plane counterpart of [back]; its content stays dark. */
    fun backPrivate(target: NestedLinkable, cost: Quantity): Commitment = actWith(::publishDark) { me -> me.back(target, cost) }

    /**
     * Author a **sealed** post (Milestone 13): like [postPrivate], the committed content is not gossiped as plaintext —
     * but instead of pure omission it is sealed under a fresh content-key and the ciphertext ([SealedContent]) is
     * replicated, so out-of-circle nodes can custody it (§20.1) without reading. The author keeps the key locally and
     * discloses it to recipients by [recommend] (disclosure = forwarding). This node, holding the key, reads its own
     * sealed post normally.
     */
    fun postSealed(text: String, cost: Quantity): Post = actWith(::publishSealed) { me -> me.share(Utf8.encodeDirect(text), cost) }

    /** Publish and back one root Post in [actor]'s public `actor -> MEANING` space as the local actor. */
    fun profile(actor: Actor, content: NestedLinkable, cost: Quantity): Post
    = act { me -> ActorProfile(actor).post(content).also { me.back(it, cost) }}

    /** Publish and back one UTF-8-readable root Post in [actor]'s public meaning space as the local actor. */
    fun profile(actor: Actor, text: String, cost: Quantity): Post = profile(actor, Utf8.encodeDirect(text), cost)

    /**
     * Ensure [text] is the local actor's top-ranked profile appearance, out-bidding any other appearance.
     *
     * The display name is a derived reading: among an actor's `actor -> MEANING` expressions (which topologically do
     * not distinguish a name from an ordinary root post), the one with the most net backing wins. Renaming therefore
     * cannot retract a prior name — it is indistinguishable from a post — so this publishes just enough backing for
     * [text] to overtake the current field by a margin of [amount] (default one unit).
     *
     * Returns true only when a new commitment was appended. The guarantee is scoped to overtaking the field *now*:
     * backing placed by other actors is honoured, so a peer can still out-bid this name later (by design).
     */
    fun ensureLocalProfile(text: String, amount: Quantity): Boolean {
        require(amount.isPositive()) { "A local profile commitment must carry positive weight." }
        val target = with(social) { ActorProfile(localActor).post(Utf8.encodeDirect(text)) }
        val appearances = localView().snapshot.profileOf(localActor)
        val targetBacking = appearances.firstOrNull { it.post == target }?.backing ?: Quantity.ZERO
        // Highest net backing among the other text-readable appearances the display-name selection ranks against.
        val rivalBacking = appearances
            .filter { it.post != target && Utf8.render(it.post.content()) != null }
            .maxOfOrNull { it.backing } ?: Quantity.ZERO
        if (targetBacking > rivalBacking) return false
        profile(localActor, target.content(), rivalBacking + amount - targetBacking)
        return true
    }

    /** Resume local authoring from the sole verified frontier head for this node's credential. */
    private fun resumeLocalJournal() {
        val credentialProjection = projector.project(localActor.link.requireNestedTarget())
        val headProjections = store.headsOf(credentialProjection)
        if (headProjections.isEmpty()) return
        require(headProjections.size == 1) {
            "Local actor has ${headProjections.size} commitment heads; choose a branch before authoring."
        }
        val projection = headProjections.single()
        val head = when (val resolved = Commitment.resolve(projection, projector, store.resolver())) {
            is Observation.Known -> resolved.value
            is Observation.Missing -> error("Local frontier head is missing preimage ${resolved.projection}.")
            is Observation.Invalid -> error("Local frontier head is invalid: ${resolved.reason}")
        }
        social.resume(head)
    }

    private fun publish(head: Commitment) {
        // publishCommitment re-derives the head's whole ancestry closure; only the records this action *newly* adds to
        // the store are the delta worth gossiping. Ancestry already present is skipped by the store's own dedup. We
        // just authored these, so they are admitted rather than re-judged (see [commitLocal]).
        onLocalEvents(commitLocal(publishCommitment(head, identity, projector)))
    }

    /**
     * Publish [head] as a *private* post (or a private back on one): apply its whole closure locally (so this node's own
     * view resolves the body and amount), but gossip only the dark header (header + attestation) and mark the committed
     * content withheld so the catch-up walk skips it too ([withheldContent] / [isContentWithheld]). Peers see an
     * attributable missing observation until [reveal].
     *
     * The commitment is filed under `project(head.targetLink)` — for a post that is the post's own board ref; for a back
     * it is the *backed* post's board ref. So a post and every private back on it share one key, and [reveal] of the post
     * naturally carries its weight across. The author names a private post by that same board ref.
     */
    private fun publishDark(head: Commitment) {
        val published = publishCommitment(head, identity, projector)
        commitLocal(published)
        published.content.forEach { withheldContent.add(it.projection) }
        privatePosts.getOrPut(projector.project(head.targetLink)) { mutableSetOf() }.add(projector.project(head.link))
        onLocalEvents(published.darkEvents())
    }

    /**
     * Publish [head] as a *sealed* post: apply its full closure locally (so this node reads its own content), but
     * withhold the plaintext from gossip and instead seal the content closure under a fresh content-key and replicate
     * the ciphertext ([SealedContent]) for custody. The key is held locally ([verifiedSeals]) and disclosed only by [recommend].
     */
    private fun publishSealed(head: Commitment) {
        val published = publishCommitment(head, identity, projector)
        commitLocal(published)
        published.content.forEach { withheldContent.add(it.projection) }   // plaintext never gossiped (only the cipher)

        val contentProjection = projector.project(CommitmentContent(head.targetLink, head.quantity).link)
        val key = Capability(ByteString(*SecureRandom.bytes(SecretBox.KEY_SIZE)))
        val nonce = ByteString(*SecureRandom.bytes(SecretBox.NONCE_SIZE))
        val sealed = SealedContent.seal(contentProjection, published.content, key, nonce, sealCodec)
        verifiedSeals[contentProjection] = key                             // we authored it, so it is proven readable
        apply(sealed)                                                      // custody our own ciphertext too

        onLocalEvents(published.darkEvents() + sealed)                     // gossip dark header + the ciphertext
    }

    /**
     * If [post] is sealed content we hold a key for — whether we authored it or learned the key from an inbound
     * disclosure — wrap that key (bound to its content) to [to] and emit a directed disclosure envelope. Disclosure is
     * transitive (review Blocker 2): a forwarder discloses by exactly the same act as an author.
     *
     * We hold keys by *content projection*, so we recover [post]'s by recomputing it from the post's own commitment —
     * resolvable precisely because we can read the post we are forwarding — and match it against [verifiedSeals]. A post
     * we hold no key for (or cannot read) yields no match and no envelope, exactly like a plain recommend.
     */
    private fun discloseSealedTo(post: Post, to: Actor) {
        val recipientSpki = Ed25519Credential.spkiOf(to) ?: return
        for (commitment in localView().backers(post)) {
            val contentProjection = projector.project(CommitmentContent(commitment.targetLink, commitment.quantity).link)
            val key = verifiedSeals[contentProjection] ?: continue
            val nonce = ByteString(*SecureRandom.bytes(SecretBox.NONCE_SIZE))
            val wrap = CapabilityWrap.of(key, contentProjection, recipientSpki, identity, nonce)
            onLocalEnvelope(DirectedEnvelope(Codec.base64Url(recipientSpki.toByteArray()), wrap))
            return
        }
    }

    /**
     * Consume a received [DirectedEnvelope]: if its wrap opens with this node's identity, hold the recovered capability
     * so [localView] can now read the matching sealed content. A relay passing through (a wrap not boxed to us) is a
     * no-op — relaying happens in the transport, never here.
     *
     * The [recipientPeerId][DirectedEnvelope.recipientPeerId] check is only a fast-path filter, not the authorization
     * (review P3): [CapabilityWrap.open] is the sole authority — it succeeds only for the addressee the box was sealed
     * to and only if the wrap is still bound to its [contentProjection][CapabilityWrap.contentProjection]. The early
     * return just avoids an unwrap attempt for an envelope flooded past us toward someone else; a forged/stale peerId
     * therefore costs at most a missed delivery (≡ a dropped packet on this best-effort mesh), never a wrong read.
     */
    fun receiveEnvelope(envelope: DirectedEnvelope) {
        if (envelope.recipientPeerId != identity.peerId) return
        val capability = envelope.wrap.open(identity) ?: return
        val contentProjection = envelope.wrap.contentProjection

        // [CapabilityWrap.open] proves only "this envelope is for me, bound to this contentProjection" — NOT that the
        // recovered key actually unlocks that content. An unconditional store would let any peer *clobber* our working
        // key (a liveness DoS, no leak). Gate on the key making the content genuinely resolve, exactly as the read path
        // does: decrypting *some* ciphertext is not enough — a peer can plant a possession-only [SealedContent] claiming
        // this contentProjection whose plaintext is junk, then box us its junk key, and that key would "open" that
        // carrier. So we require the recovered closure to *resolve to Known* at [contentProjection] through
        // [CapabilityResolver] — which re-derives every record's projection from its endpoints (`resolveLink`), a check
        // only the real content's preimages can pass (forging a record at P needs P's plaintext, i.e. the secret).
        if (resolvesWith(contentProjection, capability)) {
            // Proven readable: file by the content projection so a later recommend can re-wrap (disclose onward) this key,
            // dropping any stale pending candidate. Only a *newly* proven key churns a re-render.
            pendingSeals.remove(contentProjection)
            if (verifiedSeals.put(contentProjection, capability) != capability) onCapabilityLearned()
        } else if (contentProjection !in verifiedSeals) {
            // Unverifiable yet: either the ciphertext has not synced (a key-before-ciphertext reorder) or this is a junk
            // wrap for content we do not hold. Park it inert — never over a proven key, never feeding the resolver, never
            // firing a callback (so junk wraps cannot spam re-renders). [apply] re-verifies it when the seal arrives.
            rememberPending(contentProjection, capability)
        }
    }

    /** Whether [capability] makes [contentProjection] resolve to [Observation.Known] over our currently-held ciphertext. */
    private fun resolvesWith(contentProjection: LinkProjection, capability: Capability): Boolean =
        projector.resolveLink(
            contentProjection,
            CapabilityResolver(store.resolver(), store.sealedContents().toList(), mapOf(contentProjection to capability), sealCodec),
        ) is Observation.Known

    /**
     * Park an unverifiable candidate key alongside any others already parked for [contentProjection] — never overwriting
     * one, since with the ciphertext still missing there is no way to tell a genuine key from junk yet, and a single slot
     * would let a later arrival silently discard an earlier, possibly-genuine one. Bounded two ways: a per-projection cap
     * ([MAX_PENDING_CANDIDATES], oldest-candidate eviction) so repeated junk at one projection cannot grow without limit,
     * and the outer map ([MAX_PENDING_SEALS], oldest-projection eviction) for the same reason across projections.
     */
    private fun rememberPending(contentProjection: LinkProjection, capability: Capability) {
        val isNewProjection = contentProjection !in pendingSeals
        val candidates = pendingSeals.getOrPut(contentProjection) { mutableListOf() }
        if (capability in candidates) return
        candidates.add(capability)
        if (candidates.size > MAX_PENDING_CANDIDATES) candidates.removeAt(0)
        if (isNewProjection && pendingSeals.size > MAX_PENDING_SEALS) {
            pendingSeals.keys.firstOrNull()?.let(pendingSeals::remove)   // mutableMapOf preserves insertion order
        }
    }

    /**
     * Re-verify every candidate parked for [contentProjection] now that a seal claiming it has arrived, promoting
     * whichever one genuinely resolves into [verifiedSeals] (firing [onCapabilityLearned]) and discarding the rest —
     * including any junk that arrived in either order relative to the genuine key, since all candidates were kept. If
     * none resolves yet, they stay parked: the arrived seal may itself be a planted junk carrier while the genuine
     * ciphertext is still en route, and a later [apply] of that ciphertext re-runs this same check.
     */
    private fun promotePending(contentProjection: LinkProjection) {
        val candidates = pendingSeals[contentProjection] ?: return
        val genuine = candidates.firstOrNull { resolvesWith(contentProjection, it) } ?: return
        pendingSeals.remove(contentProjection)
        if (verifiedSeals.put(contentProjection, genuine) != genuine) onCapabilityLearned()
    }

    /**
     * Intercept sealed-ciphertext arrivals to re-verify any pending key that was waiting on this content (review §2).
     *
     * Hooked on the arrival, not on [apply], so a seal that arrives inside a validated batch promotes its pending key
     * exactly as one applied singly does — the batch path commits without going through [apply] at all.
     */
    override fun onApplied(event: ReplicatedEvent) {
        super.onApplied(event)
        if (event is SealedContent) promotePending(event.contentProjection)
    }

    /**
     * Reveal the private post named by [postRef] (its board ref projection) and every private back on it: gossip the
     * previously-withheld committed content and stop withholding it, so peers resolve the missing observation into the
     * full post with its weight. Returns false if [postRef] is not one of this node's private posts.
     */
    fun reveal(postRef: LinkProjection): Boolean {
        val heads = privatePosts.remove(postRef) ?: return false
        val revealed = mutableListOf<ReplicatedEvent>()
        for (h in heads) {
            val commitment = resolveCommitment(h) ?: continue
            publishCommitment(commitment, identity, projector).content.forEach {
                if (withheldContent.remove(it.projection)) revealed.add(it)
            }
        }
        if (revealed.isNotEmpty()) onLocalEvents(revealed)
        return true
    }

    /**
     * Forget the committed **content** of the commitment named by [head]: evict its content closure (body + amount) from
     * the store while keeping its spine and attestation, so the commitment stays permanently attributable and merely
     * degrades to a missing observation locally. Returns the number of content preimages removed (0 if [head] is unknown
     * or its content is already forgotten).
     *
     * The node side of local forgetting (roadmap M10): content-only, never the spine — a commitment's existence is
     * required for journal validity and is never deleted. Forgetting is **local and reversible**: re-applying the content
     * (re-synced from a peer that still holds it, e.g. via [import]) re-awakens it. Works for any commitment, local or
     * remote — it needs no signature, only the content-addressed projections of the same [CommitmentContent] node that
     * `publishCommitment` splits off the spine. A record shared with the spine closure is kept (never evicted).
     */
    fun forget(head: LinkProjection): Int {
        val commitment = resolveCommitment(head) ?: return 0
        val spine = projector.projectedRecords(commitment.link).mapTo(HashSet()) { it.projection }
        val content = projector.projectedRecords(CommitmentContent(commitment.targetLink, commitment.quantity).link)
        return content.count { it.projection !in spine && store.removePreimage(it.projection) }
    }

    /** Whether [projection] is a committed-content record the local actor is withholding (a private post). */
    fun isContentWithheld(projection: LinkProjection): Boolean = projection in withheldContent

    /** Board ref projections of the local actor's current private posts — for the renderer to flag and to name in reveal. */
    fun privatePostRefProjections(): Set<LinkProjection> = privatePosts.keys.toSet()

    /** Re-file [heads] (private commitment heads) at boot; re-derives their withheld content and post grouping. */
    fun restorePrivate(heads: Iterable<LinkProjection>) {
        for (h in heads) resolveCommitment(h)?.let { c ->
            publishCommitment(c, identity, projector).content.forEach { withheldContent.add(it.projection) }
            privatePosts.getOrPut(projector.project(c.targetLink)) { mutableSetOf() }.add(h)
        }
    }

    /** Every private commitment head as a base64url line, for the host's private sidecar (see [restorePrivateHeads]). */
    fun encodePrivateHeads(): List<String> = privatePosts.values.flatten().map { Codec.base64Url(it.payload.toByteArray()) }

    /** Restore private commitment heads from [lines] written by [encodePrivateHeads]; re-derives content + grouping. */
    fun restorePrivateHeads(lines: Iterable<String>) =
        restorePrivate(lines.mapNotNull { it.trim().takeIf(String::isNotEmpty) }
            .map { LinkProjection(projector.scheme, ByteString(*Codec.base64UrlDecode(it))) })

    /**
     * Every *proven* content-key as a `contentProjection<space>key` base64url line, for the host's seal sidecar (see
     * [restoreSeals]). These are device-local secrets — like the node's own identity key — so the sidecar carries the
     * same on-disk sensitivity: it must live beside the identity, never on the wire. Only [verifiedSeals] is written;
     * an unverifiable [pendingSeals] candidate is transient (it re-arrives, or its ciphertext never comes) and is not
     * worth persisting. Without this a disclosed reader goes dark again on restart — it still custodies the ciphertext,
     * but the key that unlocked it lived only in memory.
     */
    fun encodeSeals(): List<String> = verifiedSeals.map { (projection, capability) ->
        Codec.base64Url(projection.payload.toByteArray()) + " " + Codec.base64Url(capability.key.toByteArray())
    }

    /** Restore proven content-keys from [lines] written by [encodeSeals]; the sidecar is trusted local storage, so a
     *  well-formed line is filed straight into [verifiedSeals]. Malformed lines are tolerated (skipped), as on any restore. */
    fun restoreSeals(lines: Iterable<String>) {
        for (line in lines) {
            val parts = line.trim().split(' ')
            if (parts.size != 2) continue
            runCatching {
                val projection = LinkProjection(projector.scheme, ByteString(*Codec.base64UrlDecode(parts[0])))
                verifiedSeals[projection] = Capability(ByteString(*Codec.base64UrlDecode(parts[1])))
            }
        }
    }

    private fun resolveCommitment(head: LinkProjection): Commitment? =
        (Commitment.resolve(head, projector, store.resolver()) as? Observation.Known)?.value

    // ───── local view ─────

    /**
     * Assemble the local social view from the accepted event sets, opening any sealed content this node holds a key for.
     *
     * The resolver is the store's, decorated with a [CapabilityResolver] over the sealed ciphertexts + [verifiedSeals]: a
     * sealed post we hold a proven key for resolves like an ordinary post, one we lack the key for stays a missing
     * observation (and is flagged sealed-but-locked in [board]).
     */
    fun localView(): LocalSocialView =
        LocalSocialView.from(localActor, projector, store, records = capabilityAwareResolver())

    /**
     * The store's records widened by whatever sealed content this node holds a proven key for.
     *
     * With no key or nothing sealed there is nothing to widen, and the store's own resolver already *is* this view —
     * returning it unchanged is not just one less indirection: that resolver memoizes its resolutions across calls
     * ([InMemoryReplicatedEventStore.resolver]), so consecutive view builds — one per synced advance in a live
     * renderer — stop re-walking every actor log from scratch. A decorated resolver is a wider, key-dependent view and
     * deliberately keeps its own short-lived memo instead.
     */
    private fun capabilityAwareResolver(): ProjectedLinkRecordResolver {
        if (verifiedSeals.isEmpty()) return store.resolver()
        val sealed = store.sealedContents().toList()
        if (sealed.isEmpty()) return store.resolver()
        return CapabilityResolver(store.resolver(), sealed, verifiedSeals, sealCodec)
    }

    /** UI- and agent-ready projection of the current [localView]. */
    fun board(): SocialBoard = SocialBoard.from(localView(), projector)

    private companion object {
        /** Cap on parked (unverifiable) disclosure keys, so junk wraps whose ciphertext never arrives cannot grow the map. */
        const val MAX_PENDING_SEALS = 256
        /** Cap on distinct unverifiable candidates parked per projection, so repeated junk at one projection cannot grow it. */
        const val MAX_PENDING_CANDIDATES = 8
    }
}
